import { api, unwrap } from '../../shared/api/client';
import type { components, operations } from '../../shared/api/schema.gen';

export type TRun = components['schemas']['RunListItemDto'];
export type TRunList = components['schemas']['RunListResDto'];
export type TRunDetail = components['schemas']['RunDetailResDto'];
export type TRunFacets = components['schemas']['RunFacetsResDto'];
export type TFacetCount = components['schemas']['FacetCountDto'];
export type TCopy = components['schemas']['CopyListItemDto'];
export type TRunStatus = TRun['status'];
export type TCopyStatus = TCopy['status'];
export type TRunQuery = NonNullable<operations['RunsController_list']['parameters']['query']>;
export type TRunChart = components['schemas']['RunChartResDto'];
export type TRunChartBucket = components['schemas']['RunChartBucketDto'];
export type TRunChartQuery = NonNullable<operations['RunsController_chart']['parameters']['query']>;

export type TCopyTrace = components['schemas']['CopyTraceResDto'];
export type TTraceStep = components['schemas']['TraceStepDto'];
export type TNodeOverlay = components['schemas']['NodeOverlayDto'];
export type TCopyRelative = components['schemas']['CopyRelativeDto'];
export type TNodeRef = components['schemas']['NodeRefDto'];
export type TStepIo = components['schemas']['StepIoResDto'];
export type TAttemptDetail = components['schemas']['AttemptDetailDto'];
export type TExchange = components['schemas']['ExchangeDto'];
export type TReplayRun = components['schemas']['ReplayRunResDto'];
export type TStopRun = components['schemas']['StopRunResDto'];
export type TNodeRetry = components['schemas']['NodeRetryResDto'];
export type TRedeliverRefusal = components['schemas']['RedeliverRefusalDto'];
export type TBookmarkRun = components['schemas']['BookmarkRunReqDto'];
export type TRunBookmark = components['schemas']['BookmarkResDto'];

export const listRuns = (query: TRunQuery): Promise<TRunList> =>
  unwrap(api.GET('/v1/runs', { params: { query } }));

export type TRunFacetsQuery = NonNullable<
  operations['RunsController_facets']['parameters']['query']
>;
export type TRunScan = components['schemas']['ScanProgressResDto'];

export const runFacets = (query: TRunFacetsQuery): Promise<TRunFacets> =>
  unwrap(api.GET('/v1/runs/facets', { params: { query } }));

export const runChart = (query: TRunChartQuery): Promise<TRunChart> =>
  unwrap(api.GET('/v1/runs/chart', { params: { query } }));

// `at` keeps every read below on the one partition the run or copy lives in.
export const getRun = (id: string, at?: string): Promise<TRunDetail> =>
  unwrap(api.GET('/v1/runs/{id}', { params: { path: { id }, query: at ? { at } : {} } }));

export const replayRun = (id: string, at?: string): Promise<TReplayRun> =>
  unwrap(
    api.POST('/v1/runs/{id}/replay', {
      params: { path: { id }, query: at ? { at } : {} },
      body: {},
    }),
  );

export const stopRun = (id: string, at?: string): Promise<TStopRun> =>
  unwrap(api.POST('/v1/runs/{id}/stop', { params: { path: { id }, query: at ? { at } : {} } }));

export const bookmarkRun = (id: string, body: TBookmarkRun, at?: string): Promise<TRunBookmark> =>
  unwrap(
    api.POST('/v1/runs/{id}/bookmark', {
      params: { path: { id }, query: at ? { at } : {} },
      body,
    }),
  );

export const copyTrace = (id: string, at?: string): Promise<TCopyTrace> =>
  unwrap(api.GET('/v1/events/{id}/trace', { params: { path: { id }, query: at ? { at } : {} } }));

export const stepIo = (id: string, key: string, at?: string): Promise<TStepIo> =>
  unwrap(
    api.GET('/v1/events/{id}/nodes/{key}', {
      params: { path: { id, key }, query: at ? { at } : {} },
    }),
  );

export const stepAttempts = (id: string, key: string, at?: string): Promise<TAttemptDetail[]> =>
  unwrap(
    api.GET('/v1/events/{id}/nodes/{key}/attempts', {
      params: { path: { id, key }, query: at ? { at } : {} },
    }),
  );

/** `sendAgain`: a repeat on purpose — the server refuses a second redelivery without it. */
export const retryStep = (
  id: string,
  key: string,
  at?: string,
  sendAgain = false,
): Promise<TNodeRetry> =>
  unwrap(
    api.POST('/v1/events/{id}/nodes/{key}/retry', {
      params: { path: { id, key } },
      body: { ...(at ? { at } : {}), ...(sendAgain ? { sendAgain } : {}) },
    }),
  );

type TBulkPreviewDto = components['schemas']['BulkOperationPreviewResDto'];
type TBulkOperationDto = components['schemas']['BulkOperationResDto'];

/** A time window of one flow, optionally one step and one destination: what to redeliver. */
export interface IRedeliverScope {
  flowId: string;
  nodeKey?: string;
  destinationId?: string;
  from: string;
  to: string;
}

const redeliverBody = (scope: IRedeliverScope, dryRun: boolean) => ({
  params: { path: { id: scope.flowId } },
  body: {
    nodeKey: scope.nodeKey,
    destinationId: scope.destinationId,
    from: scope.from,
    to: scope.to,
    dryRun,
    // Deprecated and unread by the server (the job has its own batch size); still required.
    limit: 500,
  },
});

export const previewFlowRedeliver = async (scope: IRedeliverScope): Promise<TBulkPreviewDto> =>
  (await unwrap(
    api.POST('/v1/flows/{id}/copies/retry', redeliverBody(scope, true)),
  )) as TBulkPreviewDto;

export const startFlowRedeliver = async (scope: IRedeliverScope): Promise<TBulkOperationDto> =>
  (await unwrap(
    api.POST('/v1/flows/{id}/copies/retry', redeliverBody(scope, false)),
  )) as TBulkOperationDto;

export const runKeys = {
  all: ['runs'] as const,
  list: (query: TRunQuery) => ['runs', 'list', query] as const,
  facets: (query: TRunFacetsQuery) => ['runs', 'facets', query] as const,
  chart: (query: TRunChartQuery) => ['runs', 'chart', query] as const,
  detail: (id: string) => ['runs', 'detail', id] as const,
  trace: (copyId: string) => ['runs', 'trace', copyId] as const,
  io: (copyId: string, key: string) => ['runs', 'io', copyId, key] as const,
  attempts: (copyId: string, key: string) => ['runs', 'attempts', copyId, key] as const,
};
